import React, { useId, useMemo, useState } from 'react';
import { MapPin, X, Brain, ChevronDown } from 'lucide-react';
import { getAdminRealm } from '@/lib/adminAuth';
import { focusCitiesForRealm, focusCountryForRealm } from '@/data/focusCities';

interface Props {
  value: string;
  onChange: (city: string) => void;
  /** e.g. "Step 2" / "Step 3" — shown in the label. */
  stepLabel: string;
  /** What the city controls in this step. */
  description: string;
  disabled?: boolean;
}

/**
 * OPTIONAL focus-city text box for the Social Media Hub. Admins can type any
 * town/city or pick one from the realm's suggestion list. Leaving it blank
 * keeps the normal behaviour (the AI chooses the destination). When filled,
 * generation pulls in everything the brain has learned (see
 * buildBrainKnowledge in src/lib/brandStudio.ts).
 */
const FocusCityPicker: React.FC<Props> = ({ value, onChange, stepLabel, description, disabled }) => {
  const realm = getAdminRealm();
  const cities = focusCitiesForRealm(realm);
  const country = focusCountryForRealm(realm);
  const listId = useId();
  const [showAll, setShowAll] = useState(false);

  const visibleChips = useMemo(() => (showAll ? cities : cities.slice(0, 10)), [cities, showAll]);
  const active = value.trim().length > 0;

  return (
    <div
      className={`border p-5 transition-colors ${
        active ? 'border-[#C9A961] bg-[#FAF8F3]' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <label
            htmlFor={`${listId}-input`}
            className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] flex items-center gap-2"
          >
            <MapPin className="w-3.5 h-3.5" /> {stepLabel} · Focus City
            <span className="normal-case tracking-normal text-slate-400 font-normal">(optional)</span>
          </label>
          <p className="font-sans text-xs text-slate-600 mt-1.5 max-w-2xl">{description}</p>
        </div>
        {active && (
          <span className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 bg-[#0D1B2A] text-[#C9A961]">
            <Brain className="w-3 h-3" /> Full brain on
          </span>
        )}
      </div>

      <div className="mt-3 relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          id={`${listId}-input`}
          type="text"
          list={listId}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Type or choose a city in ${country} — leave blank to let the AI choose`}
          className="w-full border border-slate-300 pl-9 pr-10 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400 focus:outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/20 disabled:bg-slate-100 disabled:text-slate-400"
          autoComplete="off"
        />
        <datalist id={listId}>
          {cities.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        {active && !disabled && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Clear focus city"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {cities.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {visibleChips.map((c) => {
            const selected = value.trim().toLowerCase() === c.toLowerCase();
            return (
              <button
                key={c}
                type="button"
                disabled={disabled}
                onClick={() => onChange(selected ? '' : c)}
                className={`font-sans text-[11px] px-2.5 py-1 border rounded-full transition-colors disabled:opacity-50 ${
                  selected
                    ? 'bg-[#C9A961] border-[#C9A961] text-[#0D1B2A]'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#0D1B2A]'
                }`}
              >
                {c}
              </button>
            );
          })}
          {cities.length > 10 && (
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="font-sans text-[11px] px-2 py-1 text-[#8B6F47] hover:text-[#0D1B2A] inline-flex items-center gap-1"
            >
              {showAll ? 'Fewer' : `+${cities.length - 10} more`}
              <ChevronDown className={`w-3 h-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      )}

      <p className="mt-3 font-sans text-[11px] text-slate-500">
        {active ? (
          <>
            Content will be built around <strong className="text-[#0D1B2A]">{value.trim()}</strong> using everything
            the brain has learned — every trained brand voice, all Step 1 learning-database notes (city-specific notes
            first) and past posts about {value.trim()} so angles aren't repeated.
          </>
        ) : (
          <>Not required. Leave empty and the AI picks the destination as usual.</>
        )}
      </p>
    </div>
  );
};

export default FocusCityPicker;
